// TranscriptCanvas (UI-C3), JumpToLivePill (UI-C11), ReconnectBanner (UI-C12) and EngineStatusBanner (UI-C20).
// Full height, newest at the bottom, pinned to the live edge; only a person's input un-pins it (T-302).
// The canvas never blanks: a dropped connection shows a slim banner above the conversation, which stays.
// The banners share ONE slot above the canvas (one slot, one message); RoomScreen picks which one shows. The mode
// notice (UI-C17, MAJLIS-63) ranks after the socket and engine banners.
import { useEffect, useLayoutEffect, useRef, type ReactNode } from "react";
import { dirOf, useI18n } from "../i18n";
import { ScrollPin } from "../room/scrollPin";
import { ArrowDownIcon } from "./icons";

export function TranscriptCanvas({
  children,
  empty,
  onPinnedChange,
  jumpRef,
  contentKey,
  live = true,
}: {
  /** MAJLIS-67 (UI-S3): false for a finished majlis's record - the same canvas, STATIC: it is not pinned to a live edge
   * (the record opens at its start and is read from the top) and it is not a live region (nothing arrives). */
  live?: boolean;
  children: ReactNode;
  empty: boolean;
  onPinnedChange: (pinned: boolean) => void;
  /** receives the "back to live" action for the pill */
  jumpRef: { current: (() => void) | null };
  /** changes whenever the content may have changed size */
  contentKey: unknown;
}) {
  const { t } = useI18n();
  const box = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const pin = useRef<ScrollPin | null>(null);
  const onChange = useRef(onPinnedChange);
  useEffect(() => {
    onChange.current = onPinnedChange;
  }, [onPinnedChange]);

  useEffect(() => {
    if (!live) return;
    const el = box.current!;
    const p = new ScrollPin(el, (pinned) => onChange.current(pinned));
    pin.current = p;
    jumpRef.current = () => p.jumpToLive();
    const detach = p.attach();
    const grow = () => p.onGrowth();
    const ro = typeof ResizeObserver === "function" ? new ResizeObserver(grow) : null;
    ro?.observe(el);
    if (inner.current) ro?.observe(inner.current);
    // a web font swapping in (font-display: swap) re-lays out the text: hold the live edge
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    fonts?.addEventListener?.("loadingdone", grow);
    return () => {
      detach();
      ro?.disconnect();
      fonts?.removeEventListener?.("loadingdone", grow);
      jumpRef.current = null;
    };
  }, [jumpRef, live]);

  // new or changed bubbles: keep the live edge before paint
  useLayoutEffect(() => {
    pin.current?.onGrowth();
  }, [contentKey]);

  return (
    <div
      className="canvas"
      ref={box}
      role={live ? "log" : "region"}
      aria-live={live ? "polite" : undefined}
      aria-relevant={live ? "additions" : undefined}
      aria-label={t("canvas")}
      tabIndex={0}
      data-live={live}
      data-spec="UI-C3 C-2.5"
    >
      <div className="canvas-inner" ref={inner}>
        {empty && <p className="canvas-empty">{t("canvasEmpty")}</p>}
        {children}
      </div>
    </div>
  );
}

export function JumpToLivePill({ speaker, fresh, onJump }: { speaker: string | null; fresh: number; onJump: () => void }) {
  const { t } = useI18n();
  const text = speaker ? t("jumpSpeaking", { name: speaker }) : fresh > 0 ? t("jumpNew", { n: fresh }) : t("jumpLive");
  return (
    <button type="button" className="jump-pill" onClick={onJump} data-spec="UI-C11">
      <ArrowDownIcon />
      <span dir="auto">{text}</span>
    </button>
  );
}

/** UI-C20: the room's writing is paused (the engine is down), or has just come back ("again", for
 * WRITING_AGAIN_MS). Never inside a bubble; never says "engine" or "transcription". No animation: nothing to
 * honour or undo under prefers-reduced-motion. */
export function EngineStatusBanner({ state }: { state: "paused" | "again" }) {
  const { lang, t } = useI18n();
  return (
    <div
      className={`engine-banner ${state}`}
      role="status"
      aria-live="polite"
      lang={lang}
      dir={dirOf(lang)}
      data-state={state}
      data-spec="UI-C20"
    >
      {state === "paused" ? t("writingPaused") : t("writingAgain")}
    </div>
  );
}

/** UI-C17 (MAJLIS-63): the one-line notice that the host switched the room's mode, in the one banner slot. */
export function ModeNotice({ mode }: { mode: "live" | "screen_only" }) {
  const { lang, t } = useI18n();
  return (
    <div
      className="engine-banner mode-notice"
      role="status"
      aria-live="polite"
      lang={lang}
      dir={dirOf(lang)}
      data-mode={mode}
      data-spec="UI-C17"
    >
      {mode === "live" ? t("modeNoticeOn") : t("modeNoticeOff")}
    </div>
  );
}

/** MAJLIS-44 (D-34, the mock-up's 2d and 3a): a confirmed change of a seat's speaking language, once, in the one
 * banner slot at its lowest rank. This phone: "You now speak {lang}"; every other: "{name} now speaks {lang}". */
export function SpeakNotice({ own, name, lang: language }: { own: boolean; name: string; lang: string }) {
  const { lang, t } = useI18n();
  return (
    <div
      className="engine-banner speak-notice"
      role="status"
      aria-live="polite"
      lang={lang}
      dir={dirOf(lang)}
      data-spec="D-34 UI-C12 P-17b"
    >
      {own ? t("speakNoticeSelf", { lang: language }) : t("speakNoticeOther", { name, lang: language })}
    </div>
  );
}

export function ReconnectBanner({ offline }: { offline: boolean }) {
  const { t } = useI18n();
  return (
    <div className="reconnect-banner" role="status" data-spec="UI-C12 P-13">
      {offline ? t("offlineReconnecting") : t("reconnecting")}
    </div>
  );
}
